/*
  Version 1.0.2
  From M2 Mobile /component/Common/Tabs
*/

import classNames from 'classnames';
import React, { useState, useEffect, useContext } from 'react';

// 運用useContext將State共享於Tabs & TabPane
const TabsContext = React.createContext();

function useTabContext() {
  return useContext(TabsContext);
}


/**
 * Tab生成component
 * @param {object} props 
 * @param {string} props.id *required 該Tab的id，作為identifier 
 * @param {string} props.className  
 * @param {string} props.defaultTabIndex 初始顯示的tab index
 * @param {string} props.type switcher (按鈕切換式) 
 * @param {Boolean} props.inkVisible 是否要能夠看到ink(下底線)
 * @param {Boolean} props.dynamicInkWidth ink寬度是否根據tab內容長度
 * @param {string} props.activeTab 從父層控制顯示的tab時欲顯示的tab index
 * @param {function} props.onChange 切換tab時執行的callback
 * @returns 
 */
export function Tabs (props){
  const {
    id,
    className,
    defaultTabIndex,
    type,
    inkVisible = true,
    dynamicInkWidth = false,
    activeTab,
    onChange = ()=>{},
    children,
  } = props;

  const [visibleTabIndexNumber, setVisibleTabIndexNumber] = useState(0); //activeTab的index no.
  const [visibleTabIndex, setVisibleTabIndex] = useState(defaultTabIndex || children[0]?.props?.tabIndex); //activeTab的index name
  const [inkPosition, setInkPosition] = useState(null); //ink位置數值
  const [inkColor, setInkColor] = useState("#FFF"); //ink顏色
  const [tabWidth, setTabWidth] = useState(0); //tab寬度
  const [dynamicDecorationWidth, setDynamicDecorationWidth] = useState(0); //動態寬度for裝飾 (ink, switcher)
  const [switcherPosition, setSwitcherPosition] = useState(0); //switcher位置數值
  

  const contextStore = {
    visibleTabIndex,
    setVisibleTabIndex,
    tabWidth,
    setInkColor
  };

  /**
   * 設定相關style
   * @returns 
   */
  const setRelatedStyle = () => {
    const activeTabEl = document.querySelector(`#${id} .tabPane_titleBox.active`);
    const tabListEl = document.querySelector(`#${id} .tabLists`);

    const activeTabDimension = activeTabEl.getClientRects()[0];
    const tabListDimension = tabListEl.getClientRects()[0];
    
    const activeTabWidth = activeTabDimension.width;
    const switcherPosition = activeTabDimension.left - tabListDimension.left;
    const inkPosition = activeTabDimension.left  - tabListDimension.x + activeTabWidth / 2
    
    if(!activeTabEl){
      return;
    } 
    setDynamicDecorationWidth(activeTabWidth)
    setSwitcherPosition(switcherPosition);
    setInkPosition(inkPosition);
  }

  /**
   * 返回頂部
   */
  const toScrollToTop = () => {
    const cloestParent = document.querySelector(`#${id}`).parentElement;
    cloestParent.scrollTo({top:0});
    window.scrollTo({top:0});
  };

  useEffect(()=>{
    const tabListEl = document.querySelector(`#${id} .tabLists`);
    const tabListWidth = tabListEl.getClientRects()[0].width;
    const tabWidth = tabListWidth / children.length;
    setTabWidth(tabWidth);
  },[]);
  
  
  useEffect(()=>{
    const activeTabIndex = children.findIndex(child => child.props.tabIndex === visibleTabIndex);
    setVisibleTabIndexNumber(activeTabIndex)
    setRelatedStyle()
    onChange(visibleTabIndex);
    toScrollToTop();
  },[visibleTabIndex]);
  
  
  useEffect(()=>{
    if(activeTab && activeTab !== visibleTabIndex){
      activeTab && setVisibleTabIndex(activeTab);
    }
  }, [activeTab]);

  return (
    <TabsContext.Provider value={contextStore}>
      <div 
        id={id}
        className={classNames({
          tabs_container: true,
          [className]: className
        })}
      >
        <div 
          className={classNames({
            tabLists:true,
            switch:type==="switch"
          })}
        >
          {props.children}
          {inkVisible && (
            <div 
              className='tabInk' 
              style={{
                left:inkPosition, 
                display:inkPosition ? "block" : "none", 
                backgroundColor: inkColor,
                width: dynamicInkWidth && dynamicDecorationWidth,
              }}/>
          )}
          {dynamicInkWidth &&  <div className='buttomLine'/>}
          {type === "switch" && (
            <div className='switcher' style={{width: dynamicDecorationWidth, left: switcherPosition}}/>
          )}
        </div>
        <div 
          className="tabPanes"
          style={{transform:`translateX(${visibleTabIndexNumber * -100}%)`}}
        >
          {children.map((child, i)=>{
            const isActiveTab = i === visibleTabIndexNumber;
            return (
              <div
                className={classNames({
                  tabPane_content: true,
                  [child.props.tabIndex]:true,
                  active: isActiveTab
                })} 
                style={{height:isActiveTab ? undefined : 0}}
              >
                {child.props.children}
              </div>
            )
          })}
        </div>
      </div>
    </TabsContext.Provider>
  );
}


/**
 * 生成TanPane元件
 * @param {object} props 
 * @param {*} props.tabTitle tab顯示文字 
 * @param {string} props.tabIndex tab indentifier 
 * @param {component} props.prefixIcon 前綴圖片
 * @param {string} props.inkColor ink色碼 (ink需變色時使用)
 * @returns 
 */
export function TabPane(props){
  const {
    tabTitle,
    tabIndex,
    prefixIcon,
    inkColor,
  } = props; 
  const {visibleTabIndex, setVisibleTabIndex, tabWidth, setInkColor} = useTabContext();
  return (
    <div 
      className={classNames({
        tabPane_titleBox: true,
        active: visibleTabIndex === tabIndex,
        [tabIndex]: tabIndex,
      })}
      onClick={()=>{
        setVisibleTabIndex(tabIndex);
        setInkColor(inkColor);
      }}
      style={{flexBasis:tabWidth}}
    >
      {prefixIcon && (
        <div className='prefix_icon'>
          {prefixIcon}
        </div>
      )}
      <label
        className='tabPane_title'
      >
        {tabTitle}
      </label>
    </div>
  );
}
